/**
 * UserChatWidget — bolha flutuante de chat com o admin.
 *
 * Aparece pra qualquer usuário logado (exceto admin/advertiser).
 * Ao clicar, abre painel de chat 1:1 com o admin.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { MessageCircle, X, Send, Paperclip, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { API } from "../App";

function authHeaders() {
  const token = localStorage.getItem("token");
  return { Authorization: `Bearer ${token}` };
}

const ADMIN_EMAIL = "admin@gofood.com.br";

export default function UserChatWidget({ user }) {
  const [open, setOpen] = useState(false);
  const [adminUserId, setAdminUserId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [msgInput, setMsgInput] = useState("");
  const [sending, setSending] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  // Get admin user id (once)
  useEffect(() => {
    (async () => {
      try {
        // Try the shortcut: lookup admin by email via a search endpoint (advertiser can't)
        // Fallback: hard-code lookup by trying a common approach
        const r = await fetch(`${API}/direct-chat/threads`, { headers: authHeaders() });
        if (r.ok) {
          const d = await r.json();
          // If already has a thread with admin, use it
          const adminThread = (d.items || []).find(t =>
            t.other_user?.user_type === "admin" || t.other_user?.email === ADMIN_EMAIL
          );
          if (adminThread) {
            setAdminUserId(adminThread.other_user.id);
            setUnread(adminThread.unread_count || 0);
            return;
          }
        }
        // Fallback: pick a known admin. Endpoint público minimalista:
        const r2 = await fetch(`${API}/public/admin-contact`, { headers: authHeaders() });
        if (r2.ok) {
          const d2 = await r2.json();
          if (d2.admin_id) setAdminUserId(d2.admin_id);
        }
      } catch (_e) { /* */ }
    })();
  }, []);

  const loadUnread = useCallback(async () => {
    try {
      const r = await fetch(`${API}/direct-chat/unread-count`, { headers: authHeaders() });
      if (r.ok) {
        const d = await r.json();
        setUnread(d.unread_count || 0);
      }
    } catch (_e) { /* */ }
  }, []);

  useEffect(() => { loadUnread(); }, [loadUnread]);

  const loadMessages = useCallback(async () => {
    if (!adminUserId) return;
    try {
      const r = await fetch(`${API}/direct-chat/threads/${adminUserId}/messages?limit=100`, { headers: authHeaders() });
      if (r.ok) {
        const d = await r.json();
        setMessages(d.items || []);
      }
    } catch (_e) { /* */ }
  }, [adminUserId]);

  useEffect(() => {
    if (open && adminUserId) {
      loadMessages();
      // mark as read
      fetch(`${API}/direct-chat/threads/${adminUserId}/read`, { method: "POST", headers: authHeaders() })
        .then(() => setUnread(0));
    }
  }, [open, adminUserId, loadMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // WebSocket
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    const wsUrl = (API.replace(/^https?/, "wss").replace(/^http/, "ws")).replace("/api", "") + `/api/ws?token=${token}`;
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "direct_chat.new_message") {
            // Ignora self_echo — send() já adiciona a msg localmente
            if (data.self_echo) return;
            const msg = data.message;
            if (open && msg && (msg.sender_id === adminUserId || msg.recipient_id === adminUserId)) {
              setMessages(prev => prev.some(m => m.id === msg.id) ? prev : [...prev, msg]);
              if (msg.sender_id === adminUserId) {
                fetch(`${API}/direct-chat/threads/${adminUserId}/read`, { method: "POST", headers: authHeaders() });
              }
            } else {
              loadUnread();
              toast.info("💬 Nova mensagem do suporte GoFood", { duration: 6000 });
            }
          }
        } catch (_e) { /* */ }
      };
      return () => { try { ws.close(); } catch (_e) { /* noop */ } };
    } catch (_e) { /* */ }
  }, [open, adminUserId, loadUnread]);

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/direct-chat/upload`, {
        method: "POST", headers: authHeaders(), body: fd,
      });
      if (!r.ok) throw new Error("Erro no upload");
      const d = await r.json();
      setAttachment({ url: d.url, type: d.type });
    } catch (e) { toast.error(e.message); }
    finally { setUploading(false); }
  };

  const send = async () => {
    if (!adminUserId) {
      toast.error("Não foi possível iniciar chat com o suporte. Tente novamente.");
      return;
    }
    if (!msgInput.trim() && !attachment) return;
    setSending(true);
    try {
      const body = {
        text: msgInput.trim() || null,
        attachment_url: attachment?.url,
        attachment_type: attachment?.type,
      };
      const r = await fetch(`${API}/direct-chat/threads/${adminUserId}/messages`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error("Erro ao enviar");
      const d = await r.json();
      setMessages(prev => prev.some(m => m.id === d.message.id) ? prev : [...prev, d.message]);
      setMsgInput("");
      setAttachment(null);
    } catch (e) { toast.error(e.message); }
    finally { setSending(false); }
  };

  const formatTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  };

  if (!user || user.user_type === "admin") return null;

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-6 md:bottom-6 z-50 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-full w-14 h-14 shadow-2xl hover:scale-105 transition-transform flex items-center justify-center"
          data-testid="user-chat-fab"
          aria-label="Abrir chat com suporte"
        >
          <MessageCircle className="w-6 h-6"/>
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 bg-[#25D366] text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center border-2 border-white" data-testid="user-chat-badge">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed bottom-24 right-6 md:bottom-6 z-50 w-[360px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-3rem)] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border" data-testid="user-chat-panel">
          <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white p-3 flex items-center gap-2">
            <MessageCircle className="w-5 h-5"/>
            <div className="flex-1">
              <div className="font-bold text-sm">Suporte GoFood</div>
              <div className="text-[10px] text-white/80">Fale direto com nossa equipe</div>
            </div>
            <button onClick={() => setOpen(false)} className="hover:bg-white/10 rounded p-1" data-testid="btn-close-chat">
              <X className="w-4 h-4"/>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50" data-testid="user-chat-messages">
            {messages.length === 0 && (
              <div className="text-center text-xs text-gray-400 py-8">
                <p>👋 Olá! Envie sua primeira mensagem e responderemos em breve.</p>
              </div>
            )}
            {messages.map(m => {
              const mine = m.sender_id === user?.id;
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 ${mine ? "bg-orange-500 text-white" : "bg-white border shadow-sm"}`}>
                    {m.attachment_url && m.attachment_type === "image" && (
                      <img src={m.attachment_url} alt="anexo" className="rounded mb-1 max-h-32" />
                    )}
                    {m.text && <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>}
                    <div className={`text-[10px] mt-0.5 ${mine ? "text-orange-100" : "text-gray-400"}`}>{formatTime(m.created_at)}</div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
          <div className="border-t p-2">
            {attachment && (
              <div className="flex items-center gap-2 p-1 bg-orange-50 rounded mb-1 text-xs">
                <ImageIcon className="w-3 h-3 text-orange-700"/>
                <span className="flex-1 truncate">Imagem anexada</span>
                <button onClick={() => setAttachment(null)}><X className="w-3 h-3"/></button>
              </div>
            )}
            <div className="flex items-end gap-1">
              <label className="p-2 hover:bg-gray-100 rounded cursor-pointer">
                <Paperclip className="w-4 h-4 text-gray-600"/>
                <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && uploadFile(e.target.files[0])} disabled={uploading}/>
              </label>
              <textarea
                value={msgInput} onChange={e => setMsgInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Digite sua mensagem…" rows="1"
                className="flex-1 border rounded-full px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-400"
                data-testid="user-chat-input"
              />
              <button
                onClick={send} disabled={sending || uploading}
                className="bg-orange-500 hover:bg-orange-600 text-white rounded-full w-9 h-9 flex items-center justify-center"
                data-testid="user-chat-send"
              >
                <Send className="w-4 h-4"/>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
